"use client"

import { useState, useEffect, useCallback } from "react"
import { CollectionCard } from "@/components/collection-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Search, BookOpen, Sparkles } from "lucide-react"
import { Header } from "@/components/header"

interface Collection {
  id: string
  title: string
  description: string
  tagline?: string
  category: string
  isFeatured: boolean
  visitCount: number
  books: Array<{
    book: {
      id: string
      title: string
      author: string
      coverUrl?: string
    }
  }>
  _count: {
    books: number
  }
}

export default function CollectionsPage() {
  const [collections, setCollections] = useState<Collection[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  const fetchCollections = useCallback(async (pageNum = 1, searchQuery = "", reset = false) => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '12'
      })

      // Add search parameter if provided
      if (searchQuery && searchQuery.trim()) {
        params.append('search', searchQuery.trim())
      }

      const response = await fetch(`/api/collections?${params}`)
      if (response.ok) {
        const data = await response.json()
        
        if (reset) {
          setCollections(data.collections)
        } else {
          setCollections(prev => [...prev, ...data.collections])
        }
        
        setHasMore(data.pagination.page < data.pagination.pages)
      }
    } catch (error) {
      console.error('Error fetching collections:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial load and when search term changes
  useEffect(() => {
    setPage(1)
    fetchCollections(1, searchTerm, true)
  }, [searchTerm, fetchCollections])

  const loadMore = () => {
    // Only load more if not searching (search returns all results)
    if (!searchTerm || !searchTerm.trim()) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchCollections(nextPage, searchTerm, false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary/10 via-purple-500/10 to-pink-500/10">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">Thematic Collections</h1>
          </div>
          <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
            Discover carefully curated book collections designed around powerful themes and life-changing concepts. 
            Each collection is crafted to provide you with a comprehensive learning journey.
          </p>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8 border-b">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search collections by name, description, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Collections Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading && collections.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-80 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : collections.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {collections.map((collection) => (
                  <CollectionCard key={collection.id} collection={collection} />
                ))}
              </div>

              {/* Load More Button - Only show when not searching */}
              {hasMore && (!searchTerm || !searchTerm.trim()) && (
                <div className="text-center mt-12">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load More Collections'}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No collections found</h3>
              <p className="text-muted-foreground">
                {searchTerm 
                  ? `No collections match "${searchTerm}"`
                  : 'No collections available'
                }
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
